import { LoaderCircle, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { toast } from "sonner";

import type { SessionInfo } from "../shared/contracts";
import { AppShell } from "./components/app-shell";
import { Brand } from "./components/brand";
import { Button } from "./components/ui/button";
import { AuthProvider } from "./hooks/use-auth";
import { DashboardProvider } from "./hooks/use-dashboard";
import { api, getErrorMessage, setUnauthorizedHandler } from "./lib/api";
import { ActivityPage } from "./pages/activity";
import { ConnectionPage } from "./pages/connection";
import { HomePage } from "./pages/home";
import { LiveTradePage } from "./pages/live-trade";
import { LoginPage } from "./pages/login";
import { SetupPage } from "./pages/setup";
import { StrategyPage } from "./pages/strategy";

interface BootstrapState {
  loading: boolean;
  setupRequired: boolean | null;
  session: SessionInfo;
  error: string | null;
}

const initialState: BootstrapState = {
  loading: true,
  setupRequired: null,
  session: { authenticated: false },
  error: null,
};

export function App() {
  const [state, setState] = useState<BootstrapState>(initialState);

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: null }));
    try {
      const [setup, session] = await Promise.all([api.setupStatus(), api.session()]);
      setState({ loading: false, setupRequired: setup.setupRequired, session, error: null });
    } catch (caught) {
      setState((current) => ({ ...current, loading: false, error: getErrorMessage(caught) }));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!state.session.authenticated) {
      setUnauthorizedHandler(null);
      return;
    }
    return setUnauthorizedHandler(() => {
      setState((current) => ({ ...current, session: { authenticated: false }, error: null }));
      toast.error("Your session expired. Sign in again to continue.");
    });
  }, [state.session.authenticated]);

  if (state.loading) return <AppBoot />;
  if (state.error || state.setupRequired == null) return <BootstrapError message={state.error ?? "Application status is unavailable."} onRetry={() => void load()} />;

  if (state.setupRequired) {
    return (
      <Routes>
        <Route path="*" element={<SetupPage onComplete={(session) => setState({ loading: false, setupRequired: false, session, error: null })} />} />
      </Routes>
    );
  }

  if (!state.session.authenticated) {
    return (
      <Routes>
        <Route path="*" element={<LoginPage onComplete={(session) => setState((current) => ({ ...current, session }))} />} />
      </Routes>
    );
  }

  async function logout() {
    try {
      await api.logout(state.session.csrfToken);
      setState((current) => ({ ...current, session: { authenticated: false } }));
    } catch (caught) {
      toast.error(getErrorMessage(caught));
    }
  }

  return (
    <AuthProvider value={{ session: state.session, onLogout: logout }}>
      <DashboardProvider>
        <Routes>
          <Route element={<AppShell />}>
            <Route index element={<HomePage />} />
            <Route path="trade" element={<LiveTradePage />} />
            <Route path="activity" element={<ActivityPage />} />
            <Route path="strategy" element={<StrategyPage />} />
            <Route path="connection" element={<ConnectionPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </DashboardProvider>
    </AuthProvider>
  );
}

function AppBoot() {
  return (
    <main className="grid min-h-screen place-items-center bg-background px-6">
      <div className="grid justify-items-center gap-5 text-center">
        <Brand />
        <LoaderCircle className="size-5 animate-spin text-muted-foreground" aria-label="Loading Hyper Profits" />
      </div>
    </main>
  );
}

function BootstrapError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <main className="grid min-h-screen place-items-center bg-background px-6">
      <div className="grid max-w-md justify-items-center gap-5 text-center">
        <Brand />
        <div><h1 className="font-display text-2xl font-bold tracking-[-0.03em]">Hyper Profits could not start</h1><p className="mt-2 text-sm leading-6 text-muted-foreground">{message}</p></div>
        <Button variant="outline" onClick={onRetry}><RefreshCw data-icon="inline-start" /> Try again</Button>
      </div>
    </main>
  );
}
